import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  clearStoredPunditPurohitSession,
  getPunditPurohitAuthHeaders,
  getPunditPurohitToken,
  getStoredPunditPurohitUser,
  setStoredPunditPurohitUser,
} from '../utils/auth';
import AuthModal from './AuthModal';

const Navbar = () => {
  const [user, setUser] = useState(null);
  const { cart, setIsOpen } = useContext(CartContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [authModal, setAuthModal] = useState({ open: false, mode: 'signin' });
  
  const navigate = useNavigate();
  const location = useLocation(); // Listen for route changes

  const refreshUser = useCallback(() => {
    const parsedUser = getStoredPunditPurohitUser();
    const token = getPunditPurohitToken();
    if (parsedUser && token) {
      setUser(parsedUser);
      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/profile`, {
          headers: getPunditPurohitAuthHeaders(),
        })
        .then((res) => {
          const refreshedUser = {
            ...parsedUser,
            name: res.data.name || parsedUser.name,
            email: res.data.email,
            mobile: res.data.mobile,
            isPundit: res.data.isPundit,
            punditApprovalStatus: res.data.punditApprovalStatus,
          };
          setStoredPunditPurohitUser(refreshedUser);
          setUser(refreshedUser);
        })
        .catch((err) => {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            clearStoredPunditPurohitSession();
            setUser(null);
          }
        });
    } else {
      setUser(null);
    }
  }, []);

  // Check for user session whenever the route changes
  useEffect(() => {
    refreshUser();
  }, [location.pathname, refreshUser]); // Re-run check when user navigates

  const openAuthModal = (mode = 'signin') => {
    setIsUserDropdownOpen(false);
    setIsMenuOpen(false);
    setAuthModal({ open: true, mode });
  };

  const closeAuthModal = () => setAuthModal({ open: false, mode: 'signin' });

  const handleAuthSuccess = () => {
    refreshUser();
    closeAuthModal();
  };

  useEffect(() => {
    const handleOpenAuth = (event) => {
      setIsUserDropdownOpen(false);
      setIsMenuOpen(false);
      setAuthModal({ open: true, mode: event.detail?.mode || 'signin' });
    };
    window.addEventListener('pp-open-auth', handleOpenAuth);
    return () => window.removeEventListener('pp-open-auth', handleOpenAuth);
  }, []);

  const handleSignOut = () => {
    clearStoredPunditPurohitSession();
    setUser(null);
    setIsUserDropdownOpen(false);
    navigate('/uc/panditpurshothit');
  };

  return (
    <nav className="amazon-nav">
      {/* Left Section */}
      <div className="nav-left">
        <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <div className={`hamburger ${isMenuOpen ? 'open' : ''}`}>
            <span></span><span></span><span></span>
          </div>
        </button>
        <Link to="/uc/panditpurshothit" className="nav-logo">
          <div className="logo-brand">Pandit <span>Purohit</span></div>
          <div className="logo-tagline">Divine Rituals</div>
        </Link>
      </div>

      {/* Right Section */}
      <div className="nav-right">
        <Link
          to="/2.0/udyogconnect-home"
          className="nav-uc-home"
          aria-label="Back to the Udyog Connect main site"
          title="Back to the Udyog Connect main site"
        >
          <span className="nav-uc-home-logo" aria-hidden="true">UC</span>
          <span className="nav-uc-home-copy">
            <strong>Main Site</strong>
            <small>Udyog Connect</small>
          </span>
        </Link>

        {/* User Account Menu */}
        <div className="nav-user-menu-container">
          <button
            type="button"
            className="nav-link user-trigger" 
            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            aria-label={user ? `Open account menu for ${user.name}` : 'Open sign-in and account menu'}
            aria-expanded={isUserDropdownOpen}
          >
            <span className="user-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0" />
              </svg>
            </span>
            <div className="nav-text-group">
              <span className="nav-line-1">{user ? `Hello, ${user.name}` : 'Welcome'}</span>
              <span className="nav-bold">{user ? 'My account' : 'Sign in'} <span aria-hidden="true">⌄</span></span>
            </div>
          </button>

            <AnimatePresence>
              {isUserDropdownOpen && (
                <>
                  <div className="dropdown-backdrop" onClick={() => setIsUserDropdownOpen(false)} />
                  <motion.div 
                    className="user-dropdown"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                  >
                    <div className="dropdown-arrow"></div>
                    
                    {!user ? (
                      /* --- GUEST VIEW --- */
                      <>
                        <div className="dropdown-signin-prompt">
                          <button 
                            className="primary-btn-sm" 
                            onClick={() => openAuthModal('signin')}
                          >
                            Sign In
                          </button>
                          <p>New customer? <button type="button" className="auth-link-button" onClick={() => openAuthModal('signup')}>Start here.</button></p>
                        </div>
                        
                        <hr className="dropdown-divider" />
                        
                        <div className="dropdown-links locked-section">
                          <h5 className="locked-header">Your Account <span>(Login required)</span></h5>
                          <div className="dropdown-item-disabled">Your Profile</div>
                          <div className="dropdown-item-disabled">Your Orders</div>
                          <div className="dropdown-item-disabled">Saved Addresses</div>
                        </div>
                      </>
                    ) : (
                      /* --- LOGGED IN VIEW --- */
                      <>
                        <div className="dropdown-user-info">
                          <strong>{user.name}</strong>
                          <p>{user.email}</p>
                        </div>
                        <hr className="dropdown-divider" />
                        <div className="dropdown-links">
                          <Link to="/uc/profile" onClick={() => setIsUserDropdownOpen(false)}>Your Profile</Link>
                          <Link to="/uc/ViewOrders" onClick={() => setIsUserDropdownOpen(false)}>Your Orders</Link>
                          {user.isPundit && <Link to="/uc/ViewOrders" onClick={() => setIsUserDropdownOpen(false)}>Pundit Bookings</Link>}
                          <Link to="/uc/address-book" onClick={() => setIsUserDropdownOpen(false)}>Saved Addresses</Link>
                          <hr className="dropdown-divider" />
                          <button className="signout-btn" onClick={handleSignOut}>Sign Out</button>
                        </div>
                      </>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
        </div>

        {/* Cart Icon */}
        <button
          type="button"
          className="nav-cart"
          onClick={() => setIsOpen(true)}
          aria-label={`Open cart with ${cart.length} ${cart.length === 1 ? 'item' : 'items'}`}
        >
          <span className="cart-icon-wrapper" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 1.9-1.4L21 8H7" />
              <circle cx="10" cy="20" r="1.2" />
              <circle cx="18" cy="20" r="1.2" />
            </svg>
            <span className="cart-count">{cart.length}</span>
          </span>
          <span className="nav-cart-label">Cart</span>
        </button>
      </div>

      {/* Expandable Side Menu */}
      {/* Expandable Side Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div 
              className="nav-menu-overlay" 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
            />
            <motion.div 
              className="nav-side-menu"
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'tween' }}
            >
              <div className="menu-header">
                Hello, {user ? user.name : 'Sign In'}
              </div>
              <div className="menu-content">
                <h4>Trending</h4>
                <Link to="/uc/pooja-library" onClick={() => setIsMenuOpen(false)}>
                  Pooja Knowledge Library
                </Link>
                
                <hr />

                {user ? (
                  /* --- LOGGED IN SIDEBAR --- */
                  <>
                    <h4>Help & Settings</h4>
                    <Link to="/uc/ViewOrders" onClick={() => setIsMenuOpen(false)}>Your Orders</Link>
                    {user.isPundit && <Link to="/uc/ViewOrders" onClick={() => setIsMenuOpen(false)}>Pundit Bookings</Link>}
                    <Link to="/uc/profile" onClick={() => setIsMenuOpen(false)}>Account Settings</Link>
                    <Link to="/uc/address-book" onClick={() => setIsMenuOpen(false)}>Saved Addresses</Link>
                    <button className="menu-logout-btn" onClick={handleSignOut}>Sign Out</button>
                  </>
                ) : (
                  /* --- GUEST SIDEBAR --- */
                  <div className="locked-sidebar-container">
                    <div className="locked-sidebar-header">
                      <h4>Help & Settings</h4>
                      <span className="locked-badge">Login required to access</span>
                    </div>
                    
                    <div className="menu-item-disabled">Your Orders</div>
                    <div className="menu-item-disabled">Account Settings</div>
                    <div className="menu-item-disabled">Saved Addresses</div>
                    
                    <hr className="menu-divider-light" />
                    
                    <button
                      className="menu-signin-cta" 
                      type="button"
                      onClick={() => openAuthModal('signin')}
                    >
                      Sign In to Your Account
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {authModal.open && (
          <AuthModal
            open={authModal.open}
            initialMode={authModal.mode}
            onClose={closeAuthModal}
            onSuccess={handleAuthSuccess}
          />
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
